// src/pages/SuspiciousTransactionAlerts.jsx
import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { fetchTransactionsThunk } from '../features/transactions/transactionThunks';
import Sidebar from '../Component/Sidebar';
import '../CSS/TransactionAlertsTable.css'; // Reusing the CSS

const SuspiciousTransactionAlerts = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const observerRef = useRef(null);
  const loadingRef = useRef(null);
  
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [videoOverlay, setVideoOverlay] = useState(null);
  const [previewThumbnails, setPreviewThumbnails] = useState({});

  // Safe defaults
  const {
    transactions = [],
    loading = false,
    error = null,
    next = null,
    count = 0,
  } = useSelector((state) => state.transactions || {});

  /* -------------------------------------------------------- */
  /* Initial load (only if we have nothing yet)               */
  /* -------------------------------------------------------- */
  useEffect(() => {
    if (transactions.length === 0) {
      dispatch(fetchTransactionsThunk({ offset: 0 }));
    }
  }, [dispatch, transactions.length]);

  /* -------------------------------------------------------- */
  /* Generate video thumbnails                                */
  /* -------------------------------------------------------- */
  useEffect(() => {
    const generateThumbnails = async () => {
      const thumbnails = {};
      
      for (const transaction of transactions) {
        if (transaction.video_evidence) {
          try {
            // In a real implementation, you might use a server-side
            // generated thumbnail or use a video service API
            thumbnails[transaction.id] = transaction.video_evidence;
          } catch (error) {
            console.error("Error generating thumbnail:", error);
          }
        }
      }
      
      setPreviewThumbnails(thumbnails);
    };
    
    if (transactions.length > 0) {
      generateThumbnails();
    }
  }, [transactions]);

  /* -------------------------------------------------------- */
  /* Infinite scrolling implementation                        */
  /* -------------------------------------------------------- */
  const loadMore = useCallback(() => {
    if (next && !loading) {
      dispatch(fetchTransactionsThunk({ offset: transactions.length }));
    }
  }, [dispatch, next, loading, transactions.length]);

  // Setup Intersection Observer for infinite scrolling
  useEffect(() => {
    const options = {
      root: null,
      rootMargin: '0px',
      threshold: 0.1
    };

    observerRef.current = new IntersectionObserver((entries) => {
      const [entry] = entries;
      if (entry.isIntersecting && next) {
        loadMore();
      }
    }, options);

    if (loadingRef.current) {
      observerRef.current.observe(loadingRef.current);
    }

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [loadMore, next]);

  /* -------------------------------------------------------- */
  /* Helpers + derived lists                                  */
  /* -------------------------------------------------------- */
  // Example definition of "suspicious":
  const suspiciousAlerts = transactions.filter((t) => {
    if (t.flagged) return true;
    if (t.content && t.content.toLowerCase().includes('nosale')) return true;
    return false;
  });

  // Search filter
  const filteredAlerts = suspiciousAlerts.filter((item) => {
    // Using optional chaining to handle possible undefined values
    const storeName = item.store?.store_name || '';
    const storeCity = item.store?.store_city || '';
    const description = item.description || '';
    const transactionId = item.transaction_id || '';
    const device = item.device || '';
    const date = item.date || '';
    
    const combined = `${storeName} ${storeCity} ${description} ${transactionId} ${device} ${date}`.toLowerCase();
    return combined.includes(searchTerm.toLowerCase());
  });

  // Navigate when clicking a row
  const handleRowClick = (id) => navigate(`/transaction/${id}`);
  
  // Handle video click for overlay
  const handleVideoClick = (e, videoUrl) => {
    e.stopPropagation();
    setVideoOverlay(videoUrl);
  };
  
  // Close video overlay
  const closeVideoOverlay = () => setVideoOverlay(null);

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return '—';
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  // Status badge component
  const StatusBadge = ({ flagged }) => {
    if (!flagged) return null;
    
    return (
      <span className="ta-status-badge">
        <span className="ta-status-dot"></span>
        Alert
      </span>
    );
  };

  // Video thumbnail component
  const VideoThumbnail = ({ transaction }) => {
    if (!transaction.video_evidence) {
      return (
        <div className="ta-no-video">
          <div className="ta-no-video-content">
            <div className="ta-no-video-icon">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.42 0-8-3.58-8-8 0-1.85.63-3.55 1.69-4.9L16.9 18.31C15.55 19.37 13.85 20 12 20zm6.31-3.1L7.1 5.69C8.45 4.63 10.15 4 12 4c4.42 0 8 3.58 8 8 0 1.85-.63 3.55-1.69 4.9z" />
              </svg>
            </div>
            <span className="ta-no-video-text">No Media</span>
          </div>
        </div>
      );
    }

    return (
      <div 
        className="ta-video-thumbnail"
        onClick={(e) => handleVideoClick(e, transaction.video_evidence)}
        style={{
          backgroundImage: previewThumbnails[transaction.id] ? 
            `linear-gradient(rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0.4)), url(${previewThumbnails[transaction.id]})` : 
            'none',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="ta-play-button">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
            <path d="M8 5v14l11-7z" />
          </svg>
        </div>
        <span className="ta-video-duration">0:15</span>
      </div>
    );
  };

  /* -------------------------------------------------------- */
  /* Render                                                   */
  /* -------------------------------------------------------- */
  return (
    <div className="index-box">
      <Sidebar />
      <div className="sta-suspicious-alerts-container">
        {/* Header Banner */}
        <div className="sta-header-banner" style={{ backgroundImage: "url('/banner/transaction.jpg')" }}>
          <div className="sta-header-banner-overlay" />
          <div className="sta-header-banner-content">
            <h1>Suspicious Transaction Alerts</h1>
          </div>
        </div>

        {/* Toolbar */}
        <div className="sta-toolbar">
          <div className="sta-toolbar-title">
            <svg className="sta-toolbar-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <span>Suspicious Transaction Alerts</span>
          </div>

          <div className="sta-toolbar-spacer" />

          <button className="sta-export-btn" onClick={() => console.log('Export to Excel')}>
            <span className="sta-export-icon">⬇</span> Export to Excel
          </button>

          <button className="sta-filters-btn" onClick={() => setShowFilters(!showFilters)}>
            <span className="sta-filters-icon">≡</span> Filters
          </button>

          <div className="sta-search-container">
            <input
              type="text"
              placeholder="Type here to Search"
              className="sta-search-input"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <button className="sta-search-btn">
              <svg className="sta-search-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </button>
          </div>
        </div>

        {/* Filters Panel */}
        {showFilters && (
          <div className="sta-filters-panel">
            <div className="sta-filters-title">Filter by Type:</div>
            <div className="sta-filters-options">
              <label className="sta-filter-option"><input type="checkbox" /> All</label>
              <label className="sta-filter-option"><input type="checkbox" /> Flagged</label>
              <label className="sta-filter-option"><input type="checkbox" /> No Sale</label>
              <label className="sta-filter-option"><input type="checkbox" /> Safe Drop</label>
            </div>
          </div>
        )}

        {/* Error state */}
        {error && (
          <div className="ta-error-container">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
            </svg>
            <p>{error}</p>
          </div>
        )}

        {/* Data table with enhanced design */}
        <div className="ta-card-content">
          {filteredAlerts.length === 0 && !loading && !error ? (
            <div className="ta-empty-state">
              <div className="ta-empty-icon">📊</div>
              <h3>No suspicious alerts to display</h3>
              <p>When suspicious transaction alerts occur, they will appear here</p>
            </div>
          ) : (
            <table className="ta-transactions-table">
              <thead>
                <tr>
                  <th className="ta-store-column">Store</th>
                  <th className="ta-transaction-column">Transaction</th>
                  <th className="ta-date-column">Date</th>
                  <th className="ta-device-column">Device</th>
                  <th className="ta-video-column">Video</th>
                </tr>
              </thead>
              <tbody>
                {filteredAlerts.map((transaction) => (
                  <tr
                    key={transaction.id}
                    className={`ta-table-row ${transaction.flagged ? 'ta-flagged-row' : ''}`}
                    onClick={() => handleRowClick(transaction.id)}
                  >
                    <td className="ta-store-cell">
                      <div className="ta-store-info">
                        {transaction.store?.image ? (
                          <img 
                            src={transaction.store.image} 
                            alt={transaction.store?.store_name || 'Store'} 
                            className="ta-store-image"
                          />
                        ) : (
                          <div className="ta-store-image-placeholder">
                            {(transaction.store?.store_name || 'S').charAt(0)}
                          </div>
                        )}
                        <div className="ta-store-details">
                          <div className="ta-store-name">{transaction.store?.store_name || '—'}</div>
                          <div className="ta-store-location">{transaction.store?.store_city || '—'}</div>
                        </div>
                      </div>
                    </td>
                    <td className="ta-transaction-cell">
                      <div className="ta-transaction-header">
                        <span className="ta-transaction-id">{transaction.transaction_id || transaction.id}</span>
                        <StatusBadge flagged={transaction.flagged} />
                      </div>
                      <div className="ta-transaction-description">{transaction.description || transaction.content || '—'}</div>
                    </td>
                    <td className="ta-date-cell">{formatDate(transaction.date)}</td>
                    <td className="ta-device-cell">
                      <div className="ta-device-info">
                        <span className="ta-device-name">{transaction.device || '—'}</span>
                      </div>
                    </td>
                    <td className="ta-video-cell">
                      <div className="ta-video-container">
                        <VideoThumbnail transaction={{
                          ...transaction, 
                          video_evidence: transaction.video_evidence || (transaction.hasVideo ? '/path/to/video.mp4' : null)
                        }} />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          
          {/* Loading indicator for infinite scroll */}
          {next && (
            <div ref={loadingRef} className="ta-loading-indicator">
              {loading ? (
                <div className="ta-loading-spinner">
                  <div className="ta-spinner"></div>
                  <span>Loading more alerts...</span>
                </div>
              ) : (
                <div className="ta-pagination-hint">
                  <span>{transactions.length} of {count} alerts</span>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Video Overlay */}
        {videoOverlay && (
          <div className="ta-video-overlay" onClick={closeVideoOverlay}>
            <div className="ta-video-modal" onClick={(e) => e.stopPropagation()}>
              <button className="ta-close-button" onClick={closeVideoOverlay}>
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
                </svg>
              </button>
              <video 
                className="ta-video-player" 
                src={videoOverlay} 
                controls 
                autoPlay
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SuspiciousTransactionAlerts;